import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import { Card, CardContent, Tabs, Tab, Grid } from "@mui/material";
import { BaseFormPage, BaseTabPanel } from "../../../../components/common/BaseForm";
import { useFormCompletion, useFormSubmission } from "../../../../hooks";
import {
  trailerValidationSchema,
  TrailerFormValues,
  TrailerOwnershipType
} from "../../validation/trailerValidationSchema";
import {
  useGetTrailerByIdQuery,
  useUpdateTrailerMutation,
  useCreateTrailerMutation,
  useGetTrailerLookupsQuery,
  UpdateTrailerRequestDto,
  CreateTrailerRequestDto,
} from "../../../../api/trailersApi";
import {
  TrailerTextField,
  TrailerNumberField,
  TrailerDateField,
  TrailerCheckboxField,
  TrailerLookupField,
} from "./TrailerFormFields";
import {
  OwnerInfoForm,
  LeaseFromForm,
  LeaseToForm,
  OwnershipSelector,
} from "../../../../components/common/OwnershipForms";
import TrailerDeviceTab from "../TrailerDeviceTab";
import TrailerDocumentsTab from "../TrailerDocumentsTab";
import TrailerPtiHistoryTab from "../TrailerPtiHistoryTab";

export interface TrailerFormLayoutProps {
  mode: "create" | "edit";
  entityId?: string;
  title: string;
  onSuccess: () => void;
}

const TrailerFormLayout: React.FC<TrailerFormLayoutProps> = ({
  mode,
  entityId,
  title,
  onSuccess,
}) => {
  const navigate = useNavigate();
  const [selectedTab, setSelectedTab] = useState(0);
  const trailerId = entityId ? parseInt(entityId, 10) : 0;

  // API hooks
  const { data: lookupData, isLoading: isLoadingLookups, error: lookupError } = useGetTrailerLookupsQuery();
  const {
    data: trailer,
    isLoading: isLoadingTrailer,
    error: trailerError,
  } = useGetTrailerByIdQuery(trailerId, { skip: mode === "create" });

  const [updateTrailer] = useUpdateTrailerMutation();
  const [createTrailer] = useCreateTrailerMutation();

  // Initialize form values
  const getInitialValues = (): TrailerFormValues => {
    if (mode === "create") {
      return {
        // General Section
        unitNumber: "",
        isActive: true,
        startDate: null,
        endDate: null,
        size: "",
        isTanker: false,
        // Lookup fields
        trailerTypeId: null,
        trailerTypeName: null,
        trailerStatusId: null,
        trailerStatusName: null,
        divisionId: null,
        divisionName: null,
        // Equipment Info Section
        color: "",
        year: "",
        vin: "",
        platesNumber: "",
        makeId: null,
        makeName: null,
        modelId: null,
        modelName: null,
        // Reefer Section
        reeferMake: "",
        reeferModel: "",
        reeferVin: "",
        purchaseDate: null,
        purchaseAmount: null,
        // Ownership Section
        ownershipType: TrailerOwnershipType.CompanyOwned,
        ownerInfo: null,
        leaseFromInfo: null,
        leaseToInfo: null,
        // Legacy ownership fields
        isCompanyOwned: true,
        isLeased: false,
        // Safety Section
        dotInspectionExpiration: null,
        platesExpirationDate: null,
        insuranceExpirationDate: null,
        insuranceValue: null,
        hasCustomWarning: false,
        // Dispatch Section
        category: "",
        // Comments Section
        comments: "",
      };
    }

    if (!trailer) {
      // fallback to create mode defaults when trailer data is not available
      return {
        // General Section
        unitNumber: "",
        isActive: true,
        startDate: null,
        endDate: null,
        size: "",
        isTanker: false,
        // Lookup fields
        trailerTypeId: null,
        trailerTypeName: null,
        trailerStatusId: null,
        trailerStatusName: null,
        divisionId: null,
        divisionName: null,
        // Equipment Info Section
        color: "",
        year: "",
        vin: "",
        platesNumber: "",
        makeId: null,
        makeName: null,
        modelId: null,
        modelName: null,
        // Reefer Section
        reeferMake: "",
        reeferModel: "",
        reeferVin: "",
        purchaseDate: null,
        purchaseAmount: null,
        // Ownership Section
        ownershipType: TrailerOwnershipType.CompanyOwned,
        ownerInfo: null,
        leaseFromInfo: null,
        leaseToInfo: null,
        // Legacy ownership fields
        isCompanyOwned: true,
        isLeased: false,
        // Safety Section
        dotInspectionExpiration: null,
        platesExpirationDate: null,
        insuranceExpirationDate: null,
        insuranceValue: null,
        hasCustomWarning: false,
        // Dispatch Section
        category: "",
        // Comments Section
        comments: "",
      };
    }

    return {
      // General Section
      unitNumber: trailer.unitNumber || "",
      isActive: trailer.isActive,
      startDate: trailer.startDate,
      endDate: trailer.endDate,
      size: trailer.size || "",
      isTanker: trailer.isTanker,
      // Lookup fields - find IDs from names when possible
      trailerTypeId: trailer.availableTrailerTypes?.find(t => t.name === trailer.trailerType)?.id || null,
      trailerTypeName: trailer.availableTrailerTypes?.find(t => t.name === trailer.trailerType) ? null : trailer.trailerType,
      trailerStatusId: trailer.availableTrailerStatuses?.find(s => s.name === trailer.trailerStatus)?.id || null,
      trailerStatusName: trailer.availableTrailerStatuses?.find(s => s.name === trailer.trailerStatus) ? null : trailer.trailerStatus,
      divisionId: trailer.availableDivisions?.find(d => d.name === trailer.division)?.id || null,
      divisionName: trailer.availableDivisions?.find(d => d.name === trailer.division) ? null : trailer.division,
      // Equipment Info Section
      color: trailer.color || "",
      year: trailer.year || "",
      vin: trailer.vin || "",
      platesNumber: trailer.platesNumber || "",
      makeId: trailer.availableTrailerMakes?.find(m => m.name === trailer.make)?.id || null,
      makeName: trailer.availableTrailerMakes?.find(m => m.name === trailer.make) ? null : trailer.make,
      modelId: trailer.availableTrailerModels?.find(m => m.name === trailer.model)?.id || null,
      modelName: trailer.availableTrailerModels?.find(m => m.name === trailer.model) ? null : trailer.model,
      // Reefer Section
      reeferMake: trailer.reeferMake || "",
      reeferModel: trailer.reeferModel || "",
      reeferVin: trailer.reeferVIN || "",
      purchaseDate: trailer.purchaseDate,
      purchaseAmount: trailer.purchaseAmount,
      // Ownership Section
      ownershipType: trailer.ownershipType || TrailerOwnershipType.CompanyOwned,
      ownerInfo: trailer.ownerInfo || null,
      leaseFromInfo: trailer.leaseFromInfo || null,
      leaseToInfo: trailer.leaseToInfo || null,
      // Legacy ownership fields
      isCompanyOwned: trailer.isCompanyOwned,
      isLeased: trailer.isLeased,
      // Safety Section
      dotInspectionExpiration: trailer.dotInspectionExpiration,
      platesExpirationDate: trailer.platesExpirationDate,
      insuranceExpirationDate: trailer.insuranceExpirationDate,
      insuranceValue: trailer.insuranceValue,
      hasCustomWarning: trailer.hasCustomWarning,
      // Dispatch Section
      category: trailer.category || "",
      // Comments Section
      comments: trailer.comments || "",
    };
  };

  const formik = useFormik<TrailerFormValues>({
    initialValues: getInitialValues(),
    validationSchema: trailerValidationSchema,
    enableReinitialize: mode === "edit", // Only reinitialize for edit mode
    onSubmit: async (values) => {
      await handleSubmit(async () => {
        const requestData = {
          // General Section
          unitNumber: values.unitNumber,
          isActive: values.isActive,
          startDate: values.startDate,
          endDate: values.endDate,
          size: values.size,
          isTanker: values.isTanker,
          // Lookup fields
          trailerTypeId: values.trailerTypeId,
          trailerTypeName: values.trailerTypeName,
          trailerStatusId: values.trailerStatusId,
          trailerStatusName: values.trailerStatusName,
          divisionId: values.divisionId,
          divisionName: values.divisionName,
          // Equipment Info Section
          color: values.color,
          year: values.year,
          vin: values.vin,
          platesNumber: values.platesNumber,
          makeId: values.makeId,
          makeName: values.makeName,
          modelId: values.modelId,
          modelName: values.modelName,
          // Reefer Section
          reeferMake: values.reeferMake,
          reeferModel: values.reeferModel,
          reeferVin: values.reeferVin,
          purchaseDate: values.purchaseDate,
          purchaseAmount: values.purchaseAmount,
          // Ownership Section
          ownershipType: values.ownershipType,
          ownerInfo: values.ownerInfo,
          leaseFromInfo: values.leaseFromInfo,
          leaseToInfo: values.leaseToInfo,
          // Legacy ownership fields
          isCompanyOwned: values.isCompanyOwned,
          isLeased: values.isLeased,
          // Safety Section
          dotInspectionExpiration: values.dotInspectionExpiration,
          platesExpirationDate: values.platesExpirationDate,
          insuranceExpirationDate: values.insuranceExpirationDate,
          insuranceValue: values.insuranceValue,
          hasCustomWarning: values.hasCustomWarning,
          // Dispatch Section
          category: values.category,
          // Comments Section
          comments: values.comments,
        };

        if (mode === "edit") {
          return await updateTrailer({ id: trailerId, trailer: requestData as UpdateTrailerRequestDto }).unwrap();
        } else {
          return await createTrailer(requestData as CreateTrailerRequestDto).unwrap();
        }
      });
    },
  });

  const completionPercentage = useFormCompletion(formik.values);
  const {
    isSubmitting,
    submitError,
    successMessage,
    showValidationErrors,
    handleSubmit
  } = useFormSubmission({
    onSuccess: () => {
      setTimeout(() => {
        onSuccess();
      }, 1500);
    },
    successMessage: mode === "edit" ? "Trailer updated successfully!" : "Trailer created successfully!",
  });

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue);
  };

  const handleBack = () => {
    navigate("/trailers");
  };

  const getErrorMessage = (error: any): string => {
    if (error?.status === 404) {
      return "Trailer not found";
    }
    if (error?.data?.message) {
      return error.data.message;
    }
    return "An error occurred while loading the trailer";
  };

  // Loading state
  if (mode === "create" && isLoadingLookups) {
    return (
      <BaseFormPage
        title={title}
        onBack={handleBack}
        formik={formik}
        isLoading={true}
      >
        <div />
      </BaseFormPage>
    );
  }

  if (mode === "edit" && (isLoadingTrailer || (!trailer && !trailerError))) {
    return (
      <BaseFormPage
        title={title}
        onBack={handleBack}
        formik={formik}
        isLoading={true}
      >
        <div />
      </BaseFormPage>
    );
  }

  // Error states
  if (mode === "create" && lookupError) {
    return (
      <BaseFormPage
        title={title}
        onBack={handleBack}
        formik={formik}
        errorMessage="Failed to load lookup data. Please refresh the page."
      >
        <div />
      </BaseFormPage>
    );
  }

  if (mode === "edit" && (trailerError || !trailer)) {
    return (
      <BaseFormPage
        title={title}
        onBack={handleBack}
        formik={formik}
        errorMessage={trailerError ? getErrorMessage(trailerError) : "Trailer not found"}
      >
        <div />
      </BaseFormPage>
    );
  }

  return (
    <BaseFormPage
      title={title}
      subtitle={mode === "edit" && trailer ? (trailer.unitNumber || `ID: ${trailer.id}`) : undefined}
      onBack={handleBack}
      formik={formik}
      isSubmitting={isSubmitting}
      successMessage={successMessage}
      errorMessage={submitError}
      completionPercentage={completionPercentage}
      showValidationErrors={showValidationErrors}
      submitButtonText={mode === "edit" ? "Save Changes" : "Create Trailer"}
    >
      {/* Tabs */}
      <Card>
        <CardContent>
          <Tabs
            value={selectedTab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{
              borderBottom: 1,
              borderColor: "divider",
              mb: 2,
              '& .MuiTabs-scrollButtons': {
                '&.Mui-disabled': { opacity: 0.3 },
              },
            }}
          >
            <Tab label="General" />
            <Tab label="Equipment Info" />
            <Tab label="Reefer" />
            <Tab label="Ownership" />
            <Tab label="Safety" />
            <Tab label="Dispatch" />
            <Tab label="Comments" />
            {mode === "edit" && <Tab label="Device" />}
            {mode === "edit" && <Tab label="Documents" />}
            {mode === "edit" && <Tab label="PTI History" />}
          </Tabs>

          {/* General Tab */}
          <BaseTabPanel value={selectedTab} index={0} idPrefix="trailer">
            <Grid container spacing={3}>
              {/* Row 1: Unit number, Is active, Is tanker, Size */}
              <Grid item xs={12} sm={6} md={3}>
                <TrailerTextField
                  name="unitNumber"
                  label="Unit Number"
                  placeholder="Enter unit number"
                  tooltip="Unique identifier for the trailer"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TrailerCheckboxField
                  name="isActive"
                  label="Is Active"
                  tooltip="Whether the trailer is currently active"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TrailerCheckboxField
                  name="isTanker"
                  label="Is Tanker"
                  tooltip="Whether this is a tanker trailer"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TrailerTextField
                  name="size"
                  label="Size"
                  placeholder="e.g., 53ft, 48ft"
                  tooltip="Physical size of the trailer"
                />
              </Grid>
              {/* Row 2: Start date, End date */}
              <Grid item xs={12} sm={6}>
                <TrailerDateField
                  name="startDate"
                  label="Start Date"
                  tooltip="When the trailer became operational"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TrailerDateField
                  name="endDate"
                  label="End Date"
                  tooltip="When the trailer will be retired"
                />
              </Grid>
              {/* Row 3: Type, Status, Division */}
              <Grid item xs={12} sm={6} md={4}>
                <TrailerLookupField
                  name="trailerType"
                  label="Trailer Type"
                  options={mode === "create" ? (lookupData?.trailerTypes || []) : (trailer?.availableTrailerTypes || [])}
                  idFieldName="trailerTypeId"
                  nameFieldName="trailerTypeName"
                  tooltip="Type of trailer (e.g., Reefer, Dry Van)"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TrailerLookupField
                  name="trailerStatus"
                  label="Trailer Status"
                  options={mode === "create" ? (lookupData?.trailerStatuses || []) : (trailer?.availableTrailerStatuses || [])}
                  idFieldName="trailerStatusId"
                  nameFieldName="trailerStatusName"
                  tooltip="Current status of the trailer"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TrailerLookupField
                  name="division"
                  label="Division"
                  options={mode === "create" ? (lookupData?.divisions || []) : (trailer?.availableDivisions || [])}
                  idFieldName="divisionId"
                  nameFieldName="divisionName"
                  tooltip="Division this trailer belongs to"
                />
              </Grid>
            </Grid>
          </BaseTabPanel>

          {/* Equipment Info Tab */}
          <BaseTabPanel value={selectedTab} index={1} idPrefix="trailer">
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6} md={4}>
                <TrailerTextField
                  name="color"
                  label="Color"
                  placeholder="e.g., White, Blue"
                  tooltip="Color of the trailer"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TrailerTextField
                  name="year"
                  label="Year"
                  placeholder="e.g., 2020"
                  tooltip="Manufacturing year"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TrailerTextField
                  name="vin"
                  label="VIN"
                  placeholder="Vehicle Identification Number"
                  tooltip="Unique vehicle identification number"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TrailerTextField
                  name="platesNumber"
                  label="License Plates"
                  placeholder="License plate number"
                  tooltip="License plate number"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TrailerLookupField
                  name="make"
                  label="Make"
                  options={mode === "create" ? (lookupData?.trailerMakes || []) : (trailer?.availableTrailerMakes || [])}
                  idFieldName="makeId"
                  nameFieldName="makeName"
                  tooltip="Manufacturer of the trailer"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TrailerLookupField
                  name="model"
                  label="Model"
                  options={mode === "create" ? (lookupData?.trailerModels || []) : (trailer?.availableTrailerModels || [])}
                  idFieldName="modelId"
                  nameFieldName="modelName"
                  tooltip="Model of the trailer"
                />
              </Grid>
            </Grid>
          </BaseTabPanel>

          {/* Reefer Tab */}
          <BaseTabPanel value={selectedTab} index={2} idPrefix="trailer">
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6} md={4}>
                <TrailerTextField
                  name="reeferMake"
                  label="Reefer Make"
                  placeholder="e.g., Carrier, Thermo King"
                  tooltip="Manufacturer of the refrigeration unit"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TrailerTextField
                  name="reeferModel"
                  label="Reefer Model"
                  placeholder="e.g., X4 7300"
                  tooltip="Model of the refrigeration unit"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TrailerTextField
                  name="reeferVin"
                  label="Reefer VIN"
                  placeholder="Reefer VIN number"
                  tooltip="VIN of the refrigeration unit"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TrailerDateField
                  name="purchaseDate"
                  label="Purchase Date"
                  tooltip="When the reefer was purchased"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TrailerNumberField
                  name="purchaseAmount"
                  label="Purchase Amount"
                  placeholder="0.00"
                  tooltip="Purchase price of the reefer"
                  min={0}
                />
              </Grid>
            </Grid>
          </BaseTabPanel>

          {/* Ownership Tab */}
          <BaseTabPanel value={selectedTab} index={3} idPrefix="trailer">
            <Grid container spacing={3}>
              <Grid item xs={12}>
                {/* Ownership Type Selector */}
                <OwnershipSelector
                  value={formik.values.ownershipType || TrailerOwnershipType.CompanyOwned}
                  onChange={(ownershipType) => {
                    formik.setFieldValue('ownershipType', ownershipType);

                    // Initialize or clear conditional fields based on ownership type
                    if (ownershipType === TrailerOwnershipType.NotCompanyOwned) {
                      // Initialize owner info structure
                      formik.setFieldValue('ownerInfo', {
                        company: '',
                        firstName: '',
                        lastName: '',
                        address: '',
                        city: '',
                        state: '',
                        zipCode: '',
                        ssnFein: '',
                        bankAccount: '',
                        contacts: '',
                        email: '',
                        website: '',
                      });
                      formik.setFieldValue('leaseFromInfo', null);
                      formik.setFieldValue('leaseToInfo', null);
                    } else if (ownershipType === TrailerOwnershipType.CompanyOwnedLeasedFrom) {
                      // Initialize lease from info structure
                      formik.setFieldValue('leaseFromInfo', {
                        lessorId: null,
                        lessorName: null,
                        startDate: null,
                        endDate: null,
                      });
                      formik.setFieldValue('ownerInfo', null);
                      formik.setFieldValue('leaseToInfo', null);
                    } else if (ownershipType === TrailerOwnershipType.CompanyOwnedLeasedTo) {
                      // Initialize lease to info structure
                      formik.setFieldValue('leaseToInfo', {
                        lesseeId: null,
                        lesseeName: null,
                        startDate: null,
                        endDate: null,
                      });
                      formik.setFieldValue('ownerInfo', null);
                      formik.setFieldValue('leaseFromInfo', null);
                    } else {
                      // Company owned - clear all ownership info
                      formik.setFieldValue('ownerInfo', null);
                      formik.setFieldValue('leaseFromInfo', null);
                      formik.setFieldValue('leaseToInfo', null);
                    }
                  }}
                  entityType="trailer"
                  error={formik.touched.ownershipType && formik.errors.ownershipType ? String(formik.errors.ownershipType) : undefined}
                />

                {/* Owner Information Form (for NotCompanyOwned) */}
                <OwnerInfoForm
                  ownershipType={formik.values.ownershipType || TrailerOwnershipType.CompanyOwned}
                  entityType="trailer"
                  TextFieldComponent={TrailerTextField}
                />

                {/* Lease From Form (for CompanyOwnedLeasedFrom) */}
                <LeaseFromForm
                  showForm={(formik.values.ownershipType || TrailerOwnershipType.CompanyOwned) === TrailerOwnershipType.CompanyOwnedLeasedFrom}
                  availableLessors={mode === "create" ? (lookupData?.lessors || []) : (trailer?.availableLessors || [])}
                  entityType="trailer"
                  TextFieldComponent={TrailerTextField}
                  DateFieldComponent={TrailerDateField}
                />

                {/* Lease To Form (for CompanyOwnedLeasedTo) */}
                <LeaseToForm
                  showForm={(formik.values.ownershipType || TrailerOwnershipType.CompanyOwned) === TrailerOwnershipType.CompanyOwnedLeasedTo}
                  availableLessees={mode === "create" ? (lookupData?.lessees || []) : (trailer?.availableLessees || [])}
                  entityType="trailer"
                  TextFieldComponent={TrailerTextField}
                  DateFieldComponent={TrailerDateField}
                />
              </Grid>
            </Grid>
          </BaseTabPanel>

          {/* Safety Tab */}
          <BaseTabPanel value={selectedTab} index={4} idPrefix="trailer">
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6} md={4}>
                <TrailerDateField
                  name="dotInspectionExpiration"
                  label="DOT Inspection Expiration"
                  tooltip="When DOT inspection expires"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TrailerDateField
                  name="platesExpirationDate"
                  label="Plates Expiration Date"
                  tooltip="When license plates expire"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TrailerDateField
                  name="insuranceExpirationDate"
                  label="Insurance Expiration Date"
                  tooltip="When insurance expires"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TrailerNumberField
                  name="insuranceValue"
                  label="Insurance Value"
                  placeholder="0.00"
                  tooltip="Insurance value of the trailer"
                  min={0}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TrailerCheckboxField
                  name="hasCustomWarning"
                  label="Has Custom Warning"
                  tooltip="Whether there are custom warnings for this trailer"
                />
              </Grid>
            </Grid>
          </BaseTabPanel>

          {/* Dispatch Tab */}
          <BaseTabPanel value={selectedTab} index={5} idPrefix="trailer">
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6} md={4}>
                <TrailerTextField
                  name="category"
                  label="Category"
                  placeholder="e.g., General, Specialized"
                  tooltip="Dispatch category for the trailer"
                />
              </Grid>
            </Grid>
          </BaseTabPanel>

          {/* Comments Tab */}
          <BaseTabPanel value={selectedTab} index={6} idPrefix="trailer">
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TrailerTextField
                  name="comments"
                  label="Comments"
                  placeholder="Additional notes about the trailer"
                  tooltip="Any additional comments or notes"
                  multiline
                  rows={4}
                />
              </Grid>
            </Grid>
          </BaseTabPanel>

          {/* Device Tab - only in edit mode */}
          {mode === "edit" && (
            <BaseTabPanel value={selectedTab} index={7} idPrefix="trailer">
              <TrailerDeviceTab trailerId={trailerId} />
            </BaseTabPanel>
          )}

          {/* Documents Tab - only in edit mode */}
          {mode === "edit" && (
            <BaseTabPanel value={selectedTab} index={8} idPrefix="trailer">
              <TrailerDocumentsTab trailerId={trailerId} />
            </BaseTabPanel>
          )}
          {mode === "edit" && (
            <BaseTabPanel value={selectedTab} index={9} idPrefix="trailer">
              <TrailerPtiHistoryTab trailerId={trailerId} />
            </BaseTabPanel>
          )}
        </CardContent>
      </Card>
    </BaseFormPage>
  );
};

export default TrailerFormLayout;
